import { createFileRoute, Link } from "@tanstack/react-router";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { AionCta } from "@/components/site/AionCta";

const URL = "https://rajshamani.life/guides/how-to-use-ai-in-business-india";
const TITLE = "How to Use AI in Your Business in India (2026 Guide)";
const DESC =
  "A practical guide for Indian founders and operators: where AI actually saves money, which workflows to automate first, what to avoid, and how to measure the return.";

const FAQS: { q: string; a: string }[] = [
  {
    q: "Where should a small Indian business start with AI?",
    a: "Start with one repetitive, text-heavy workflow — customer support replies, sales follow-ups, content drafting or invoice and document processing. Automate that one workflow end to end before touching anything else.",
  },
  {
    q: "How much does it cost to use AI in a small business?",
    a: "For most small teams the model cost is trivial — often a few thousand rupees a month. The real cost is the time spent designing the workflow and checking output quality in the first few weeks.",
  },
  {
    q: "Will AI replace my team?",
    a: "In practice it changes what each person spends their day on. The teams getting real returns in India are using AI to remove low-judgement work so the same headcount handles more volume, not to cut staff.",
  },
  {
    q: "What are the biggest AI mistakes Indian businesses make?",
    a: "Buying tools before defining the workflow, letting AI write customer-facing content with no review, ignoring data privacy, and never measuring whether anything actually got faster or cheaper.",
  },
  {
    q: "Do I need an AI engineer to do this?",
    a: "Not at the start. Most first wins come from an operator who understands the business deeply and knows how to prompt well. You need engineering once the workflow touches your own data and systems at scale.",
  },
];

export const Route = createFileRoute("/guides/how-to-use-ai-in-business-india")({
  head: () => ({
    meta: [
      { key: "title", title: TITLE },
      { key: "description", name: "description", content: DESC },
      { property: "og:title", content: TITLE, key: "og:title" },
      { property: "og:description", content: DESC, key: "og:description" },
      { property: "og:type", content: "article", key: "og:type" },
      { property: "og:url", content: URL, key: "og:url" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: TITLE, key: "twitter:title" },
      { name: "twitter:description", content: DESC, key: "twitter:description" },
      { name: "geo.region", content: "IN", key: "geo.region" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          description: DESC,
          mainEntityOfPage: URL,
          author: { "@type": "Person", name: "Raj Shamani", url: "https://rajshamani.life" },
          publisher: { "@id": "https://rajshamani.life/#organization" },
          isPartOf: { "@id": "https://rajshamani.life/#website" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: GuidePage,
});

function H2({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2 id={id} className="text-2xl md:text-3xl font-display text-gold mt-12 mb-4 scroll-mt-24">
      {children}
    </h2>
  );
}

function GuidePage() {
  return (
    <div>
      <PageBreadcrumb current="How to Use AI in Your Business in India" />
      <article className="mx-auto max-w-3xl px-6 py-16">
        <h1 className="text-4xl md:text-5xl font-display font-semibold tracking-tight leading-tight">
          How to Use AI in Your Business in India
        </h1>
        <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
          Most Indian businesses experimenting with AI in 2026 are getting nothing out
          of it, and the reason is almost always the same: they bought a tool before
          they picked a workflow. This guide is the opposite order — pick the workflow,
          measure the baseline, then automate.
        </p>

        <H2 id="first-workflow">Pick one workflow, not a strategy</H2>
        <p className="text-muted-foreground leading-relaxed">
          The highest-return first projects in Indian SMEs are almost always
          text-heavy and repetitive: replying to the same twenty customer questions,
          drafting sales follow-ups, turning WhatsApp enquiries into structured leads,
          summarising vendor documents, or generating first drafts of marketing copy.
          Choose one. Write down how long it takes today and what it costs. That number
          is how you will know whether AI worked.
        </p>

        <H2 id="build-vs-buy">Build or buy</H2>
        <p className="text-muted-foreground leading-relaxed">
          Buy when the workflow is generic and someone already sells it well — meeting
          notes, transcription, basic support deflection. Build when the workflow
          depends on your own data, your own catalogue, or your own way of selling.
          Anything that touches your competitive edge is worth building, and in 2026
          building means a few hundred lines of code, not a data-science team.
        </p>

        <H2 id="quality">Quality control is the whole job</H2>
        <p className="text-muted-foreground leading-relaxed">
          The failure mode is not a model that refuses to work; it is a model that
          confidently produces something slightly wrong at scale. Before anything goes
          in front of a customer, decide who reviews it, what "good" looks like, and
          what happens when it is wrong. Teams that skip this step end up switching AI
          off within a month.
        </p>

        <H2 id="people">What this means for your team</H2>
        <p className="text-muted-foreground leading-relaxed">
          The pattern showing up across{" "}
          <Link to="/podcast" className="text-gold underline underline-offset-4">
            conversations on Figuring Out
          </Link>{" "}
          is that AI is not removing people from Indian businesses — it is raising what
          each person is expected to produce. The operators pulling ahead are the ones
          who learned to work with these tools early, not the ones who waited for a
          company policy.
        </p>

        <H2 id="measure">Measure it like any other investment</H2>
        <p className="text-muted-foreground leading-relaxed">
          Hours saved per week, cost per transaction, response time, conversion rate on
          the automated workflow. If none of those moved after a month, the problem is
          usually the workflow choice, not the model. Kill it and pick a different one.
        </p>

        <H2 id="next">The one skill worth learning first</H2>
        <p className="text-muted-foreground leading-relaxed">
          Everything above depends on one underlying skill: being able to instruct a
          model precisely and judge its output honestly. That is learnable in weeks and
          compounds for years — which is the same argument Raj makes about{" "}
          <Link to="/build-dont-talk" className="text-gold underline underline-offset-4">
            building leverage over credentials
          </Link>
          .
        </p>
      </article>

      <AionCta
        heading="Learn to actually use AI as leverage"
        body="Aion is a cinematic AI course built for founders and operators in India — prompt engineering, AI agents and the mental models behind real output. Entry is ₹11."
      />
    </div>
  );
}
